import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveNonconformanceReport,
  closeNonconformanceReport,
  createNonconformanceReport,
  deleteNonconformanceReport,
  fetchNonconformanceReport,
  fetchNonconformanceReports,
  reopenNonconformanceReport,
  revokeNonconformanceReport,
  updateNonconformanceReport,
  downloadNonconformanceReportPdf,
} from '../../api/nonconformanceReport';
import { notifyError, reportError } from '../../utils/notify';
import { formatNumberInput, parseNumberInput } from '../../utils/numberFormat';
import { fetchCompanyInventory } from '../../api/companyInventory';
import { useCompanyStore } from '../../store/companyStore';
import { CompanyInventoryItem } from '../../types/companyInventory';
import { canConfirmInspection } from '../../types/companyRole';
import { APPROVAL_TARGET_NCR } from '../../types/approval';
import {
  NCR_CAUSE_CATEGORY_OPTIONS,
  NCR_DEFECT_CATEGORY_OPTIONS,
  NCR_DISPOSITION_OPTIONS,
  NCR_SOURCE_OPTIONS,
  NcrCreatePrefill,
  NonconformanceReport,
  NonconformanceReportPayload,
  NonconformanceStockLinePayload,
  ncrAffectsInventory,
  ncrCauseCategoryLabel,
  ncrDefectCategoryLabel,
  ncrDispositionLabel,
  ncrSourceLabel,
  ncrStatusLabel,
} from '../../types/nonconformanceReport';
import InventorySkuSelect from './InventorySkuSelect';
import MidProductionLotPicker from './MidProductionLotPicker';
import ProductionDailyLotPicker from './ProductionDailyLotPicker';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';

import '../contract/ContractPanel.css';
import './InspectionFormSheet.css';
import './NonconformanceReportPanel.css';

type View =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'detail'; reportId: number }
  | { type: 'edit'; reportId: number };

const LIST_VIEW: View = { type: 'list' };

type LotPickerTarget = { type: 'form' } | { type: 'line'; key: string };

const EMPTY: NonconformanceReportPayload = {
  reportNo: '',
  reportDate: new Date().toISOString().slice(0, 10),
  sourceType: 'OTHER',
  sourceInspectionId: null,
  inventoryId: null,
  itemName: '',
  specText: '',
  lotNo: '',
  qty: '',
  defectCategory: 'OTHER',
  causeCategory: 'METHOD',
  defectDesc: '',
  cause: '',
  disposition: 'REWORK',
  correctiveAction: '',
  status: 'OPEN',
  reporterName: '',
  remark: '',
  stockLines: [],
};

type StockLineDraft = NonconformanceStockLinePayload & { key: string };

function emptyStockLine(): StockLineDraft {
  return {
    key: `sl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    inventoryId: 0,
    itemName: '',
    specText: '',
    lotNo: '',
    qty: '',
    remark: '',
  };
}

function toStockDrafts(
  lines?: { inventoryId?: number | null; itemName?: string | null; specText?: string | null; lotNo?: string | null; qty?: string | null; remark?: string | null }[] | null,
  seed?: NcrCreatePrefill | null
): StockLineDraft[] {
  if (lines && lines.length > 0) {
    return lines
      .filter((line) => line.inventoryId != null)
      .map((line) => ({
        key: `sl-${line.inventoryId}-${Math.random().toString(36).slice(2, 6)}`,
        inventoryId: Number(line.inventoryId),
        itemName: line.itemName || '',
        specText: line.specText || '',
        lotNo: line.lotNo || '',
        qty: line.qty ? formatNumberInput(String(line.qty)) : '',
        remark: line.remark || '',
      }));
  }
  if (seed?.inventoryId != null) {
    return [
      {
        key: `sl-seed-${seed.inventoryId}`,
        inventoryId: seed.inventoryId,
        itemName: seed.itemName || '',
        specText: seed.specText || '',
        lotNo: seed.lotNo || '',
        qty: seed.qty ? formatNumberInput(String(seed.qty)) : '',
        remark: '',
      },
    ];
  }
  return [];
}

/** 헤더(대상 품목)와 같은 라인은 제외하고 추가 차감분만 편집 */
function additionalStockDrafts(
  lines: StockLineDraft[],
  header?: { inventoryId?: number | null; lotNo?: string | null; qty?: string | null } | null
): StockLineDraft[] {
  if (lines.length === 0) return [];
  if (header?.inventoryId == null) return lines;
  const headerLot = (header.lotNo || '').trim();
  const headerQty = (header.qty || '').trim();
  let primaryIdx = lines.findIndex(
    (line) =>
      line.inventoryId === header.inventoryId &&
      (line.lotNo || '').trim() === headerLot &&
      (!headerQty || (line.qty || '').trim() === headerQty)
  );
  if (primaryIdx < 0) {
    primaryIdx = lines.findIndex((line) => line.inventoryId === header.inventoryId);
  }
  if (primaryIdx < 0) return lines;
  return lines.filter((_, idx) => idx !== primaryIdx);
}

function isApproved(status?: string | null) {
  return (status || '').toUpperCase() === 'APPROVED';
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ['작성', authorName],
          ['검토', reviewerName],
          ['승인', approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span style={{ color: value ? '#1f2a37' : '#98a2b3', fontWeight: value ? 600 : 400 }}>
            {value || '-'}
          </span>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({
  status,
  approvalInProgress,
}: {
  status?: string | null;
  approvalInProgress?: boolean | null;
}) {
  const key = approvalInProgress ? 'pending' : (status || 'OPEN').toLowerCase();
  return (
    <span className={`ncr-status is-${key}`}>
      {ncrStatusLabel(status, approvalInProgress)}
    </span>
  );
}

function NonconformanceReportPanel({
  prefill = null,
  onPrefillConsumed,
  initialReportId = null,
  onInitialReportConsumed,
  contractId = null,
  embedded = false,
}: {
  prefill?: NcrCreatePrefill | null;
  onPrefillConsumed?: () => void;
  initialReportId?: number | null;
  onInitialReportConsumed?: () => void;
  contractId?: number | null;
  embedded?: boolean;
} = {}) {
  void embedded;
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const [view, setView] = useState<View>(LIST_VIEW);
  const [rows, setRows] = useState<NonconformanceReport[]>([]);
  const [detail, setDetail] = useState<NonconformanceReport | null>(null);
  const [form, setForm] = useState<NonconformanceReportPayload>(EMPTY);
  const [keyword, setKeyword] = useState('');
  const [reportNoHint, setReportNoHint] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'OPEN' | 'DRAFT' | 'CLOSED'>('ALL');
  const [inventoryItems, setInventoryItems] = useState<CompanyInventoryItem[]>([]);
  const [stockLines, setStockLines] = useState<StockLineDraft[]>([]);
  const [lotPicker, setLotPicker] = useState<LotPickerTarget | null>(null);

  const summary = useMemo(() => {
    const open = rows.filter((r) => r.status === 'OPEN').length;
    const draft = rows.filter((r) => r.status === 'DRAFT').length;
    const closed = rows.filter((r) => r.status === 'CLOSED').length;
    return { total: rows.length, open, draft, closed };
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (statusFilter === 'ALL') return rows;
    return rows.filter((row) => (row.status || 'OPEN') === statusFilter);
  }, [rows, statusFilter]);

  const loadList = useCallback(async (q?: string) => {
    try {
      setLoading(true);
      setRows(await fetchNonconformanceReports(q, contractId));
      setError('');
    } catch {
      reportError(setError, '부적합품 보고서 목록을 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  const loadInventory = useCallback(async () => {
    try {
      setInventoryItems(await fetchCompanyInventory());
    } catch {
      setInventoryItems([]);
    }
  }, []);

  const openCreate = useCallback((seed?: NcrCreatePrefill | null) => {
    const nextForm = {
      ...EMPTY,
      reportDate: seed?.reportDate || new Date().toISOString().slice(0, 10),
      sourceType: seed?.sourceType || 'OTHER',
      sourceInspectionId: seed?.sourceInspectionId ?? null,
      inventoryId: seed?.inventoryId ?? null,
      itemName: seed?.itemName || '',
      specText: seed?.specText || '',
      lotNo: seed?.lotNo || '',
      qty: seed?.qty || '',
      defectCategory: seed?.defectCategory || 'OTHER',
      causeCategory: seed?.causeCategory || 'METHOD',
      defectDesc: seed?.defectDesc || '',
      reporterName: seed?.reporterName || '',
    };
    setForm(nextForm);
    // seed 품목은 대상 품목으로 두고, 추가 차감 라인은 비움
    setStockLines([]);
    setLotPicker(null);
    setReportNoHint('저장 시 보고서 번호가 자동 발급됩니다.');
    setError('');
    setView({ type: 'create' });
    void loadInventory();
  }, [loadInventory, setView]);

  useEffect(() => {
    setView(LIST_VIEW);
    setKeyword('');
    setStatusFilter('ALL');
    void loadList();
    void loadInventory();
  }, [companyId, loadList, loadInventory, setView]);

  useEffect(() => {
    if (!prefill) return;
    void openCreate(prefill);
    onPrefillConsumed?.();
  }, [prefill, openCreate, onPrefillConsumed]);

  useEffect(() => {
    if (initialReportId == null) return;
    setView({ type: 'detail', reportId: initialReportId });
    onInitialReportConsumed?.();
  }, [initialReportId, onInitialReportConsumed]);

  useEffect(() => {
    if (view.type !== 'list') return;
    const timer = window.setTimeout(() => void loadList(keyword), 250);
    return () => window.clearTimeout(timer);
  }, [keyword, loadList, view.type]);

  useEffect(() => {
    if (view.type !== 'detail' && view.type !== 'edit') {
      setDetail(null);
      return;
    }
    void fetchNonconformanceReport(view.reportId)
      .then((data) => {
        setDetail(data);
        if (view.type === 'edit') {
          if (isApproved(data.status)) {
            setView({ type: 'detail', reportId: data.reportId });
            return;
          }
          setForm({
            reportNo: data.reportNo ?? '',
            reportDate: data.reportDate ?? '',
            sourceType: (data.sourceType as NonconformanceReportPayload['sourceType']) || 'OTHER',
            sourceInspectionId: data.sourceInspectionId ?? null,
            inventoryId: data.inventoryId ?? null,
            itemName: data.itemName ?? '',
            specText: data.specText ?? '',
            lotNo: data.lotNo ?? '',
            qty: data.qty ? formatNumberInput(String(data.qty)) : '',
            defectCategory: data.defectCategory || 'OTHER',
            causeCategory: data.causeCategory || 'METHOD',
            defectDesc: data.defectDesc ?? '',
            cause: data.cause ?? '',
            disposition: (data.disposition as NonconformanceReportPayload['disposition']) || 'REWORK',
            correctiveAction: data.correctiveAction ?? '',
            status: (data.status as NonconformanceReportPayload['status']) || 'OPEN',
            reporterName: data.reporterName ?? '',
            remark: data.remark ?? '',
          });
          setStockLines(
            additionalStockDrafts(toStockDrafts(data.stockLines), {
              inventoryId: data.inventoryId,
              lotNo: data.lotNo,
              qty: data.qty ? formatNumberInput(String(data.qty)) : '',
            })
          );
          setLotPicker(null);
          void loadInventory();
        }
        setError('');
      })
      .catch(() => reportError(setError, '부적합품 보고서를 불러오지 못했습니다.'));
  }, [view, loadInventory]);

  const goList = () => {
    setView(LIST_VIEW);
    void loadList(keyword);
  };

  const updateStockLine = (key: string, patch: Partial<StockLineDraft>) => {
    setStockLines((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const selectTargetInventory = (id: number | null) => {
    if (id == null) {
      setForm((p) => ({
        ...p,
        inventoryId: null,
        itemName: '',
        specText: '',
        lotNo: '',
      }));
      return;
    }
    const item = inventoryItems.find((row) => row.inventoryId === id);
    setForm((p) => ({
      ...p,
      inventoryId: id,
      itemName: item?.itemName || p.itemName || '',
      specText: item?.specText || '',
      lotNo: '',
    }));
  };

  const selectStockInventory = (key: string, inventoryIdRaw: string) => {
    if (!inventoryIdRaw) {
      updateStockLine(key, {
        inventoryId: 0,
        itemName: '',
        specText: '',
        lotNo: '',
      });
      return;
    }
    const inventoryId = Number(inventoryIdRaw);
    const item = inventoryItems.find((row) => row.inventoryId === inventoryId);
    updateStockLine(key, {
      inventoryId,
      itemName: item?.itemName || '',
      specText: item?.specText || '',
      lotNo: '',
    });
  };

  const applyLotPick = (
    target: LotPickerTarget,
    pick: { lotNo: string; itemName?: string; specText?: string }
  ) => {
    const lotNo = pick.lotNo.trim();
    if (target.type === 'form') {
      setForm((p) => ({
        ...p,
        lotNo,
        itemName: pick.itemName?.trim() || p.itemName,
        specText: pick.specText?.trim() || p.specText || '',
      }));
    } else {
      updateStockLine(target.key, {
        lotNo,
        ...(pick.itemName?.trim() ? { itemName: pick.itemName.trim() } : {}),
        ...(pick.specText?.trim() ? { specText: pick.specText.trim() } : {}),
      });
    }
    setLotPicker(null);
  };

  const selectedTargetInventory = useMemo(
    () => inventoryItems.find((row) => row.inventoryId === form.inventoryId) ?? null,
    [inventoryItems, form.inventoryId]
  );

  const lotPickerInventory = useMemo(() => {
    if (!lotPicker) return null;
    if (lotPicker.type === 'form') return selectedTargetInventory;
    const line = stockLines.find((row) => row.key === lotPicker.key);
    if (!line?.inventoryId) return null;
    return (
      inventoryItems.find((row) => row.inventoryId === line.inventoryId) ??
      ({
        inventoryId: line.inventoryId,
        itemName: line.itemName || '품목',
        specText: line.specText || '',
        itemKind: 'MATERIAL',
      } as CompanyInventoryItem)
    );
  }, [lotPicker, selectedTargetInventory, stockLines, inventoryItems]);

  const useFinishedLotPicker = (lotPickerInventory?.itemKind || '').toUpperCase() === 'FINISHED';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const itemName = form.itemName.trim();
    if (!itemName) {
      reportError(setError, '품목을 선택하거나 품명을 입력해 주세요.');
      return;
    }
    const primaryQty = form.qty?.trim()
      ? parseNumberInput(form.qty)
      : null;
    const additionalLines = stockLines.filter((line) => line.inventoryId > 0);
    const affects = ncrAffectsInventory(form.disposition);

    if (affects) {
      if (form.inventoryId == null || form.inventoryId <= 0) {
        reportError(setError, '폐기/반품은 대상 품목(재고)을 선택해 주세요.');
        return;
      }
      if (primaryQty == null || primaryQty <= 0) {
        reportError(setError, '폐기/반품은 대상 품목 수량을 0보다 크게 입력해 주세요.');
        return;
      }
      for (const line of additionalLines) {
        if (
          !line.qty.trim() ||
          parseNumberInput(line.qty) == null ||
          (parseNumberInput(line.qty) ?? 0) <= 0
        ) {
          reportError(setError, '추가 차감 라인의 수량은 0보다 큰 숫자로 입력해 주세요.');
          return;
        }
      }
    }

    const stockPayload: NonconformanceStockLinePayload[] = affects
      ? [
          {
            inventoryId: Number(form.inventoryId),
            itemName: form.itemName.trim() || null,
            specText: form.specText?.trim() || null,
            lotNo: form.lotNo?.trim() || null,
            qty: String(primaryQty),
            remark: null,
          },
          ...additionalLines.map((line) => ({
            inventoryId: line.inventoryId,
            itemName: line.itemName?.trim() || null,
            specText: line.specText?.trim() || null,
            lotNo: line.lotNo?.trim() || null,
            qty: String(parseNumberInput(line.qty) ?? line.qty.trim()),
            remark: line.remark?.trim() || null,
          })),
        ]
      : [];

    try {
      setSaving(true);
      const payload: NonconformanceReportPayload = {
        reportNo: form.reportNo?.trim() || null,
        reportDate: form.reportDate || null,
        sourceType: form.sourceType || 'OTHER',
        sourceInspectionId: form.sourceInspectionId || null,
        inventoryId: form.inventoryId ?? null,
        itemName,
        specText: form.specText?.trim() || null,
        lotNo: form.lotNo?.trim() || null,
        qty: form.qty?.trim()
          ? String(parseNumberInput(form.qty) ?? form.qty.trim())
          : null,
        defectCategory: form.defectCategory || null,
        causeCategory: form.causeCategory || null,
        defectDesc: form.defectDesc?.trim() || null,
        cause: form.cause?.trim() || null,
        disposition: form.disposition || null,
        correctiveAction: form.correctiveAction?.trim() || null,
        status: form.status === 'DRAFT' ? 'DRAFT' : 'OPEN',
        reporterName: form.reporterName?.trim() || null,
        remark: form.remark?.trim() || null,
        contractId: contractId ?? undefined,
        stockLines: stockPayload,
      };
      const saved =
        view.type === 'edit'
          ? await updateNonconformanceReport(view.reportId, payload)
          : await createNonconformanceReport(payload);
      if (view.type !== 'edit') {
        window.alert(`보고서 번호 ${saved.reportNo || '-'}로 등록되었습니다.`);
      }
      setView({ type: 'detail', reportId: saved.reportId });
      setDetail(saved);
      void loadList(keyword);
      setError('');
    } catch {
      reportError(setError, '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    if (!detail) return;
    if (isApproved(detail.status)) {
      reportError(setError, '승인 완료된 보고서는 종결할 수 없습니다.');
      return;
    }
    const hasStockLines = (detail.stockLines || []).some((line) => line.inventoryId != null);
    const affects = ncrAffectsInventory(detail.disposition);
    if (hasStockLines && !affects) {
      reportError(setError, '재고 차감 라인이 있습니다. 조치 구분을 폐기 또는 반품으로 바꾼 뒤 종결해 주세요.');
      return;
    }
    if (affects && !hasStockLines && detail.inventoryId == null) {
      reportError(setError, '폐기/반품 종결 시 재고 차감 라인을 1개 이상 등록해 주세요.');
      return;
    }
    const message = affects
      ? '종결하면 대상 품목(및 추가 차감 라인) 수량이 출고됩니다. 계속할까요?'
      : '이 보고서를 종결 처리할까요?';
    if (!window.confirm(message)) return;
    try {
      const updated = await closeNonconformanceReport(detail.reportId);
      setDetail(updated);
      void loadList(keyword);
      setError('');
    } catch (err: unknown) {
      const messageFromApi =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '종결 처리에 실패했습니다. 조치 구분(폐기/반품)·재고 차감 라인·잔량을 확인해 주세요.';
      reportError(setError, messageFromApi);
    }
  };

  const handleReopen = async () => {
    if (!detail) return;
    if (isApproved(detail.status)) {
      reportError(setError, '승인 완료된 보고서는 재개할 수 없습니다.');
      return;
    }
    const affects = ncrAffectsInventory(detail.disposition);
    const message = affects
      ? '재개하면 종결 때 차감했던 재고를 되돌립니다. 계속할까요?'
      : '종결을 취소하고 다시 진행중으로 둘까요?';
    if (!window.confirm(message)) return;
    try {
      const updated = await reopenNonconformanceReport(detail.reportId);
      setDetail(updated);
      void loadList(keyword);
      setError('');
    } catch (err: unknown) {
      const messageFromApi =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '재개 처리에 실패했습니다.';
      reportError(setError, messageFromApi);
    }
  };

  const handleDelete = async () => {
    if (!detail) return;
    if (isApproved(detail.status)) {
      reportError(setError, '승인 완료된 보고서는 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm('이 부적합품 보고서를 삭제할까요?')) return;
    try {
      await deleteNonconformanceReport(detail.reportId);
      goList();
    } catch {
      reportError(setError, '삭제에 실패했습니다. 종결된 보고서는 삭제할 수 없습니다.');
    }
  };

  const handleApprove = async () => {
    if (!detail) return;
    if (!canApproveDirect) {
      reportError(setError, '바로 승인은 오너/팀장만 가능합니다.');
      return;
    }
    if (
      !window.confirm(
        '부적합품 보고서를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.'
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError('');
      const saved = await approveNonconformanceReport(detail.reportId);
      setDetail(saved);
      setApprovalMessage('바로 승인되었습니다.');
      void loadList(keyword);
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '바로 승인에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (!detail) return;
    if (!window.confirm('승인을 철회할까요?\n승인란·검토란이 지워집니다.')) return;
    try {
      setApproving(true);
      setError('');
      const saved = await revokeNonconformanceReport(detail.reportId);
      setDetail(saved);
      setApprovalMessage('승인이 철회되었습니다.');
      void loadList(keyword);
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인 철회에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    try {
      setPdfDownloading(true);
      const name = detail.reportNo?.trim() || String(detail.reportId);
      await downloadNonconformanceReportPdf(detail.reportId, `부적합보고서_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.'
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const stockAffects = ncrAffectsInventory(form.disposition);

  const renderEditActions = () => (
    <div className="ncr-form__actions">
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          view.type === 'edit'
            ? setView({ type: 'detail', reportId: view.reportId })
            : goList()
        }
      >
        취소
      </button>
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        {saving ? '저장 중...' : '저장'}
      </button>
    </div>
  );

  const renderDetailActions = () => (
    <div className="ncr-actions">
      {isApproved(detail?.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void handleDownloadPdf()}
        >
          {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
        </button>
      ) : null}
      {detail?.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void handleRevoke()}
        >
          {approving ? '철회 중...' : '승인 철회'}
        </button>
      ) : null}
      {detail?.canCancelApproval ? (
        <ApprovalCancelButton
          detail={detail}
          className="ncr-btn is-secondary"
          disabled={approving}
          onCancelled={async () => {
            if (!detail) return;
            setDetail(await fetchNonconformanceReport(detail.reportId));
            setApprovalMessage('');
            void loadList();
          }}
        />
      ) : null}
      {!isApproved(detail?.status) ? (
        <>
          {detail && detail.status !== 'CLOSED' && !detail.approvalInProgress ? (
            <button
              type="button"
              className="ncr-btn is-secondary"
              onClick={() => setView({ type: 'edit', reportId: detail.reportId })}
            >
              수정
            </button>
          ) : null}
          {/* 종결 후에만 결재·승인 */}
          {detail?.status === 'CLOSED' && !detail.approvalInProgress ? (
            <button
              type="button"
              className="ncr-btn is-primary"
              disabled={approving}
              onClick={() => {
                setApprovalMessage('');
                setApprovalOpen(true);
              }}
            >
              결재 요청
            </button>
          ) : null}
          {detail?.status === 'CLOSED' &&
          !detail.approvalInProgress &&
          canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void handleApprove()}
            >
              {approving ? '승인 중...' : '승인'}
            </button>
          ) : null}
          {detail?.status === 'CLOSED' ? (
            <button
              type="button"
              className="ncr-btn is-secondary"
              disabled={!!detail.approvalInProgress}
              onClick={() => void handleReopen()}
            >
              재개
            </button>
          ) : (
            <button
              type="button"
              className="ncr-btn is-primary"
              onClick={() => void handleClose()}
            >
              종결
            </button>
          )}
          {detail && detail.status !== 'CLOSED' && !detail.approvalInProgress ? (
            <button
              type="button"
              className="ncr-btn is-danger"
              disabled={approving}
              onClick={() => void handleDelete()}
            >
              삭제
            </button>
          ) : null}
        </>
      ) : null}
      <button type="button" className="ncr-btn is-secondary" onClick={goList}>
        목록으로
      </button>
    </div>
  );

  const renderForm = () => (
    <form className="ncr-form" onSubmit={handleSubmit}>
      {renderEditActions()}
      <section className="ncr-form__section">
        <div className="ncr-form__section-head">
          <h3>문서 정보</h3>
          <p>보고서 식별·발생 단계·작성 상태를 입력합니다.</p>
        </div>
        <div className="ncr-form__group">
          <div className="ncr-form__grid">
            <label>
              보고서 번호
              <input
                value={view.type === 'create' ? '' : form.reportNo ?? ''}
                readOnly
                placeholder={view.type === 'create' ? '저장 시 자동 발급' : undefined}
                maxLength={100}
              />
              {view.type === 'create' && reportNoHint ? (
                <span className="ncr-form__hint">{reportNoHint}</span>
              ) : null}
            </label>
            <label>
              발생일
              <input
                type="date"
                value={form.reportDate ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, reportDate: e.target.value }))}
              />
            </label>
            <label>
              발생 단계
              <select
                value={form.sourceType ?? 'OTHER'}
                onChange={(e) => setForm((p) => ({ ...p, sourceType: e.target.value }))}
              >
                {NCR_SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {form.sourceInspectionId ? (
                <span className="ncr-form__hint">연결 검사 ID: {form.sourceInspectionId}</span>
              ) : null}
            </label>
            <label>
              상태
              <select
                value={form.status === 'DRAFT' ? 'DRAFT' : 'OPEN'}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="OPEN">진행중</option>
                <option value="DRAFT">작성중</option>
              </select>
            </label>
            <label>
              작성자
              <input
                value={form.reporterName ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, reporterName: e.target.value }))}
                maxLength={80}
              />
            </label>
          </div>
        </div>
      </section>

      <section className="ncr-form__section">
        <div className="ncr-form__section-head">
          <h3>대상 품목</h3>
          <p>
            재고에서 품목·LOT를 고릅니다. 폐기/반품이면 이 내용이 종결 시 기본 출고(차감)로
            반영됩니다.
          </p>
        </div>
        <div className="ncr-form__group">
          <div className="ncr-form__grid">
            <label className="is-span-2">
              재고 품목 {stockAffects ? '*' : ''}
              <InventorySkuSelect
                value={form.inventoryId || ''}
                items={inventoryItems}
                showKind
                onChange={selectTargetInventory}
                placeholder="재고에서 선택"
              />
            </label>
            <label>
              품명 *
              <input
                value={form.itemName}
                onChange={(e) => setForm((p) => ({ ...p, itemName: e.target.value }))}
                required
                maxLength={255}
                placeholder={form.inventoryId ? undefined : '재고 미선택 시 직접 입력'}
              />
            </label>
            <label>
              규격
              <input
                value={form.specText ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, specText: e.target.value }))}
                maxLength={255}
              />
            </label>
            <label>
              LOT
              <div className="ncr-lot-field">
                <input
                  value={form.lotNo ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, lotNo: e.target.value }))}
                  maxLength={100}
                  placeholder="직접 입력 또는 불러오기"
                />
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  disabled={!form.inventoryId}
                  onClick={() => setLotPicker({ type: 'form' })}
                >
                  LOT 불러오기
                </button>
              </div>
            </label>
            <label>
              수량 {stockAffects ? '*' : ''}
              <input
                type="text"
                inputMode="decimal"
                value={form.qty ?? ''}
                onChange={(e) =>
                  setForm((p) => ({ ...p, qty: formatNumberInput(e.target.value) }))
                }
                maxLength={50}
              />
            </label>
          </div>
          {stockAffects ? (
            <p className="ncr-form__hint ncr-target-stock-hint">
              종결 시 위 품목·LOT·수량이 기본으로 출고됩니다. 다른 품목을 더 빼려면 아래에서 추가
              차감 라인을 넣으세요.
            </p>
          ) : null}
        </div>
      </section>

      <section
        className={`ncr-form__section ncr-disposition-block${
          stockAffects ? ' is-stock-out' : ' is-stock-safe'
        }`}
      >
        <div className="ncr-form__section-head">
          <div>
            <h3>조치 구분 · 재고</h3>
            <p>조치에 따라 종결 시 재고 차감 여부가 달라집니다.</p>
          </div>
          <span className={`ncr-stock-badge${stockAffects ? ' is-out' : ' is-safe'}`}>
            {stockAffects ? '종결 시 재고 차감' : '재고 미차감'}
          </span>
        </div>

        <div className="ncr-form__group ncr-disposition-picker">
          <span className="ncr-form__group-label">조치 구분</span>
          <div className="ncr-disposition-options" role="radiogroup" aria-label="조치 구분">
            {NCR_DISPOSITION_OPTIONS.map((o) => {
              const affects = ncrAffectsInventory(o.value);
              const selected = (form.disposition ?? 'REWORK') === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`ncr-disposition-option${selected ? ' is-selected' : ''}${
                    affects ? ' is-stock-out' : ' is-stock-safe'
                  }`}
                  onClick={() => setForm((p) => ({ ...p, disposition: o.value }))}
                >
                  <strong>{o.label}</strong>
                  <span>{affects ? '재고 차감' : '재고 유지'}</span>
                </button>
              );
            })}
          </div>
          <p className={`ncr-disposition-banner${stockAffects ? ' is-out' : ' is-safe'}`}>
            {stockAffects
              ? '폐기·반품은 종결 시 대상 품목이 기본 출고됩니다. 중간검사에서 이미 뺀 투입과 겹치지 않게 추가분만 등록하세요.'
              : '재작업·특채·기타는 종결해도 재고가 줄지 않습니다. 추가 차감 라인은 저장되지 않습니다.'}
          </p>
        </div>

        {stockAffects ? (
          <div className="ncr-form__group ncr-stock-group is-required">
            <div className="ncr-stock-head">
              <div>
                <h4>추가 차감 라인</h4>
                <p>
                  대상 품목과 다른 재고를 더 출고할 때만 추가하세요. (선택)
                </p>
              </div>
              <button
                type="button"
                className="ncr-btn is-secondary"
                onClick={() => setStockLines((prev) => [...prev, emptyStockLine()])}
              >
                라인 추가
              </button>
            </div>
            {form.inventoryId ? (
              <div className="ncr-stock-primary">
                <strong>기본 차감 (대상 품목)</strong>
                <span>
                  {[
                    form.itemName || `재고 #${form.inventoryId}`,
                    form.specText,
                    form.lotNo ? `LOT ${form.lotNo}` : null,
                    form.qty ? `수량 ${form.qty}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            ) : (
              <div className="ncr-stock-empty is-warning">대상 품목(재고)을 먼저 선택해 주세요.</div>
            )}
            {stockLines.length === 0 ? (
              <p className="ncr-form__hint">추가 차감이 없으면 비워 두세요.</p>
            ) : (
              stockLines.map((line) => (
                <div key={line.key} className="ncr-stock-line">
                  <label className="is-span-2">
                    재고 품목 *
                    <InventorySkuSelect
                      value={line.inventoryId || ''}
                      items={inventoryItems}
                      showKind
                      onChange={(id) =>
                        selectStockInventory(line.key, id == null ? '' : String(id))
                      }
                      placeholder="선택"
                    />
                  </label>
                  <label>
                    수량 *
                    <input
                      type="text"
                      inputMode="decimal"
                      value={line.qty}
                      onChange={(e) =>
                        updateStockLine(line.key, { qty: formatNumberInput(e.target.value) })
                      }
                    />
                  </label>
                  <label>
                    LOT
                    <div className="ncr-lot-field">
                      <input
                        value={line.lotNo || ''}
                        onChange={(e) => updateStockLine(line.key, { lotNo: e.target.value })}
                        maxLength={100}
                      />
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        disabled={!line.inventoryId}
                        onClick={() => setLotPicker({ type: 'line', key: line.key })}
                      >
                        LOT 불러오기
                      </button>
                    </div>
                  </label>
                  <label>
                    비고
                    <input
                      value={line.remark || ''}
                      onChange={(e) => updateStockLine(line.key, { remark: e.target.value })}
                      maxLength={500}
                    />
                  </label>
                  <div className="ncr-stock-line__actions">
                    <button
                      type="button"
                      className="ncr-btn is-danger"
                      onClick={() =>
                        setStockLines((prev) => prev.filter((row) => row.key !== line.key))
                      }
                    >
                      삭제
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : null}
      </section>

      <section className="ncr-form__section">
        <div className="ncr-form__section-head">
          <h3>QC 카테고리</h3>
          <p>불량 유형은 파레토, 원인 분류는 특성요인도(5M1E) 집계에 사용합니다.</p>
        </div>
        <div className="ncr-form__group">
          <div className="ncr-form__grid">
            <label>
              불량 유형
              <select
                value={form.defectCategory ?? 'OTHER'}
                onChange={(e) => setForm((p) => ({ ...p, defectCategory: e.target.value }))}
              >
                {NCR_DEFECT_CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              원인 분류 (5M1E)
              <select
                value={form.causeCategory ?? 'METHOD'}
                onChange={(e) => setForm((p) => ({ ...p, causeCategory: e.target.value }))}
              >
                {NCR_CAUSE_CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </section>

      <section className="ncr-form__section">
        <div className="ncr-form__section-head">
          <h3>내용·시정</h3>
          <p>현상·원인·시정·예방 조치를 기록합니다.</p>
        </div>
        <div className="ncr-form__group">
          <div className="ncr-form__grid ncr-form__grid--stack">
            <label className="is-span-2">
              부적합 내용
              <textarea
                value={form.defectDesc ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, defectDesc: e.target.value }))}
                rows={3}
              />
            </label>
            <label className="is-span-2">
              원인
              <textarea
                value={form.cause ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, cause: e.target.value }))}
                rows={3}
              />
            </label>
            <label className="is-span-2">
              시정·예방 조치
              <textarea
                value={form.correctiveAction ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, correctiveAction: e.target.value }))}
                rows={3}
              />
            </label>
            <label className="is-span-2">
              비고
              <textarea
                value={form.remark ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, remark: e.target.value }))}
                rows={2}
              />
            </label>
          </div>
        </div>
      </section>

      {renderEditActions()}

      {lotPicker && lotPickerInventory ? (
        useFinishedLotPicker ? (
          <MidProductionLotPicker
            inventory={lotPickerInventory}
            onClose={() => setLotPicker(null)}
            onSelect={(pick) =>
              applyLotPick(lotPicker, {
                lotNo: pick.lotNo,
                itemName: pick.itemName,
                specText: pick.specText,
              })
            }
          />
        ) : (
          <ProductionDailyLotPicker
            inventory={lotPickerInventory}
            onClose={() => setLotPicker(null)}
            onSelect={(pick) =>
              applyLotPick(lotPicker, {
                lotNo: pick.lotNo,
                itemName: pick.itemName,
                specText: pick.specText,
              })
            }
          />
        )
      ) : null}
    </form>
  );

  return (
    <main className="contract-panel ncr-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>부적합품 보고서</strong>
          <span>품질 이상 · 조치 · QC 집계</span>
        </div>
        <div className="contract-panel__search">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="번호 / 품명 / LOT / 카테고리"
          />
        </div>
        <div className="contract-panel__list-wrap">
          {loading ? (
            <p className="contract-panel__hint">불러오는 중...</p>
          ) : filteredRows.length === 0 ? (
            <p className="contract-panel__hint">해당 조건의 보고서가 없습니다.</p>
          ) : (
            <ul className="contract-panel__list">
              {filteredRows.map((row) => (
                <li key={row.reportId}>
                  <button
                    type="button"
                    className={`contract-panel__list-item${
                      (view.type === 'detail' || view.type === 'edit') &&
                      view.reportId === row.reportId
                        ? ' is-active'
                        : ''
                    }`}
                    onClick={() => setView({ type: 'detail', reportId: row.reportId })}
                  >
                    <span className="contract-panel__list-name">
                      {row.itemName || '품명 없음'}
                    </span>
                    <span className="contract-panel__list-meta">
                      {row.reportNo ? `${row.reportNo} · ` : ''}
                      {ncrDefectCategoryLabel(row.defectCategory)} ·{' '}
                      <StatusBadge status={row.status} approvalInProgress={row.approvalInProgress} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      <section className="contract-panel__main">
        {view.type === 'create' || view.type === 'edit' ? (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>{view.type === 'edit' ? '부적합품 보고서 수정' : '부적합품 보고서 등록'}</h2>
                <p>카테고리를 함께 남겨 두면 이후 QC7 도구 집계에 바로 쓸 수 있습니다.</p>
              </div>
              <button type="button" className="ncr-btn is-secondary" onClick={goList}>
                목록으로
              </button>
            </div>
            {error && <p className="contract-panel__error">{error}</p>}
            <div className="contract-panel__content">{renderForm()}</div>
          </>
        ) : view.type === 'detail' ? (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>부적합품 보고서 상세</h2>
                <p>
                  {detail?.reportNo || '-'} · <StatusBadge status={detail?.status} approvalInProgress={detail?.approvalInProgress} />
                </p>
              </div>
              {renderDetailActions()}
            </div>
            {approvalMessage ? (
              <p className="contract-panel__hint" style={{ color: '#1a5fb4' }}>
                {approvalMessage}
              </p>
            ) : null}
            {detail?.approvalInProgress ? (
              <p className="contract-panel__hint">
                결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
              </p>
            ) : null}
            {error && <p className="contract-panel__error">{error}</p>}
            <div className="contract-panel__content">
              {!detail ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : (
                <div className="ncr-detail">
                  <div
                    className="ifs-sheet"
                    style={{ marginBottom: 14, padding: '12px 14px', background: '#fff' }}
                  >
                    <div className="ifs-sheet__top" style={{ marginBottom: 0 }}>
                      <div>
                        <h4 className="ifs-sheet__title" style={{ margin: 0, fontSize: 15 }}>
                          {detail.itemName || '부적합품 보고서'}
                        </h4>
                        <p className="ifs-sheet__subtitle" style={{ margin: '4px 0 0' }}>
                          {detail.reportNo || '-'} · {ncrStatusLabel(detail.status, detail.approvalInProgress)}
                        </p>
                      </div>
                      <StampBox
                        authorName={detail.authorName || detail.reporterName}
                        reviewerName={detail.reviewerName}
                        approverName={detail.approverName}
                      />
                    </div>
                  </div>
                  <section className="ncr-detail__card">
                    <h3>문서 정보</h3>
                    <dl>
                      <dt>보고서 번호</dt>
                      <dd>{detail.reportNo || '-'}</dd>
                      <dt>발생일</dt>
                      <dd>{detail.reportDate || '-'}</dd>
                      <dt>발생 단계</dt>
                      <dd>{ncrSourceLabel(detail.sourceType)}</dd>
                      <dt>연결 검사</dt>
                      <dd>{detail.sourceInspectionId ? `#${detail.sourceInspectionId}` : '-'}</dd>
                      <dt>상태</dt>
                      <dd>
                        <StatusBadge status={detail.status} approvalInProgress={detail.approvalInProgress} />
                      </dd>
                      <dt>작성자</dt>
                      <dd>{detail.reporterName || detail.authorName || '-'}</dd>
                    </dl>
                  </section>
                  <section className="ncr-detail__card">
                    <h3>대상 품목</h3>
                    <dl>
                      <dt>품명</dt>
                      <dd>{detail.itemName || '-'}</dd>
                      <dt>규격</dt>
                      <dd>{detail.specText || '-'}</dd>
                      <dt>LOT</dt>
                      <dd>{detail.lotNo || '-'}</dd>
                      <dt>수량</dt>
                      <dd>{detail.qty || '-'}</dd>
                    </dl>
                  </section>
                  <section
                    className={`ncr-detail__card is-wide ncr-detail__disposition${
                      ncrAffectsInventory(detail.disposition) ? ' is-stock-out' : ' is-stock-safe'
                    }`}
                  >
                    <div className="ncr-detail__disposition-head">
                      <h3>조치 · 재고</h3>
                      <span
                        className={`ncr-stock-badge${
                          ncrAffectsInventory(detail.disposition) ? ' is-out' : ' is-safe'
                        }`}
                      >
                        {ncrAffectsInventory(detail.disposition) ? '종결 시 재고 차감' : '재고 미차감'}
                      </span>
                    </div>
                    <dl>
                      <dt>조치 구분</dt>
                      <dd>
                        <span
                          className={`ncr-disposition-tag${
                            ncrAffectsInventory(detail.disposition) ? ' is-out' : ' is-safe'
                          }`}
                        >
                          {ncrDispositionLabel(detail.disposition)}
                        </span>
                      </dd>
                    </dl>
                    <div className="ncr-detail__stock">
                      <h4>재고 차감</h4>
                      <p className="ncr-form__hint">
                        {ncrAffectsInventory(detail.disposition)
                          ? '대상 품목이 기본 차감이며, 아래는 저장된 출고 라인입니다.'
                          : '재고 미차감 조치입니다.'}
                        {ncrAffectsInventory(detail.disposition) && detail.status === 'CLOSED'
                          ? ' (종결됨 · 재고 반영)'
                          : ncrAffectsInventory(detail.disposition) && detail.status === 'APPROVED'
                            ? ' (승인됨 · 재고 반영)'
                            : ''}
                      </p>
                      {(detail.stockLines || []).length === 0 ? (
                        <p className="ncr-form__hint">등록된 차감 라인이 없습니다.</p>
                      ) : (
                        <ul className="ncr-stock-detail">
                          {(detail.stockLines || []).map((line, idx) => (
                            <li key={line.lineId ?? `${line.inventoryId}-${idx}`}>
                              <strong>{line.itemName || `재고 #${line.inventoryId}`}</strong>
                              <span>
                                {[
                                  line.specText,
                                  line.lotNo ? `LOT ${line.lotNo}` : null,
                                  `수량 ${line.qty}`,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </section>
                  <section className="ncr-detail__card">
                    <h3>QC 카테고리</h3>
                    <dl>
                      <dt>불량 유형</dt>
                      <dd>{ncrDefectCategoryLabel(detail.defectCategory)}</dd>
                      <dt>원인 분류</dt>
                      <dd>{ncrCauseCategoryLabel(detail.causeCategory)}</dd>
                    </dl>
                  </section>
                  <section className="ncr-detail__card is-wide">
                    <h3>내용·시정</h3>
                    <dl>
                      <dt>부적합 내용</dt>
                      <dd>{detail.defectDesc || '-'}</dd>
                      <dt>원인</dt>
                      <dd>{detail.cause || '-'}</dd>
                      <dt>시정·예방 조치</dt>
                      <dd>{detail.correctiveAction || '-'}</dd>
                      <dt>비고</dt>
                      <dd>{detail.remark || '-'}</dd>
                    </dl>
                  </section>
                </div>
              )}
            </div>
            {renderDetailActions()}
            {detail ? (
              <ApprovalRequestModal
                open={approvalOpen}
                titleDefault={`${detail.itemName || '부적합품 보고서'} 결재`}
                targetType={APPROVAL_TARGET_NCR}
                targetId={detail.reportId}
                onClose={() => setApprovalOpen(false)}
                onSubmitted={() => {
                  setApprovalOpen(false);
                  setApprovalMessage(
                    '결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.'
                  );
                  void fetchNonconformanceReport(detail.reportId).then(setDetail);
                  void loadList(keyword);
                }}
              />
            ) : null}
          </>
        ) : (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>{contractId != null ? '계약 부적합품 보고서 현황' : '부적합품 보고서 현황'}</h2>
                <p>불량 유형·원인 분류를 남겨 QC7(파레토·특성요인도) 데이터로 쌓습니다.</p>
              </div>
              <button
                type="button"
                className="ncr-btn is-primary"
                onClick={() => void openCreate()}
              >
                보고서 등록
              </button>
            </div>
            {error && <p className="contract-panel__error">{error}</p>}
            <div className="contract-panel__content">
              <div className="ncr-summary" role="tablist" aria-label="상태 필터">
                <button
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === 'ALL'}
                  className={`ncr-summary__chip is-all${statusFilter === 'ALL' ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter('ALL')}
                >
                  <strong>{summary.total}</strong>
                  <span>전체</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === 'OPEN'}
                  className={`ncr-summary__chip is-open${statusFilter === 'OPEN' ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter('OPEN')}
                >
                  <strong>{summary.open}</strong>
                  <span>진행중</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === 'DRAFT'}
                  className={`ncr-summary__chip is-draft${statusFilter === 'DRAFT' ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter('DRAFT')}
                >
                  <strong>{summary.draft}</strong>
                  <span>작성중</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === 'CLOSED'}
                  className={`ncr-summary__chip is-closed${statusFilter === 'CLOSED' ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter('CLOSED')}
                >
                  <strong>{summary.closed}</strong>
                  <span>종결</span>
                </button>
              </div>
              {loading ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : rows.length === 0 ? (
                <p className="contract-panel__hint">보고서를 등록해 주세요.</p>
              ) : filteredRows.length === 0 ? (
                <p className="ncr-empty">선택한 상태의 보고서가 없습니다.</p>
              ) : (
                <div className="ncr-table-wrap">
                  <table className="ec-table contract-panel__table">
                    <thead>
                      <tr>
                        <th style={{ width: 120 }}>번호</th>
                        <th>품명</th>
                        <th style={{ width: 90 }}>단계</th>
                        <th style={{ width: 100 }}>불량유형</th>
                        <th style={{ width: 110 }}>원인분류</th>
                        <th style={{ width: 90 }}>조치</th>
                        <th style={{ width: 100 }}>발생일</th>
                        <th style={{ width: 80 }}>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row) => (
                        <tr
                          key={row.reportId}
                          onClick={() => setView({ type: 'detail', reportId: row.reportId })}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{row.reportNo || '-'}</td>
                          <td>{row.itemName || '-'}</td>
                          <td>{ncrSourceLabel(row.sourceType)}</td>
                          <td>{ncrDefectCategoryLabel(row.defectCategory)}</td>
                          <td>{ncrCauseCategoryLabel(row.causeCategory)}</td>
                          <td>
                            <span
                              className={`ncr-disposition-tag${
                                ncrAffectsInventory(row.disposition) ? ' is-out' : ' is-safe'
                              }`}
                            >
                              {ncrDispositionLabel(row.disposition)}
                            </span>
                          </td>
                          <td>{row.reportDate || '-'}</td>
                          <td>
                            <StatusBadge status={row.status} approvalInProgress={row.approvalInProgress} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default NonconformanceReportPanel;
